/* eslint-disable import/no-extraneous-dependencies */
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, FormHelperText } from '@mui/material';
import { Editor as TinyMCE } from '@tinymce/tinymce-react';
import type { Editor as TinyMCEEditor } from 'tinymce';
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/plugins/paste';
import 'tinymce/plugins/link';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/image';
import 'tinymce/plugins/table';
import 'tinymce/plugins/media';
import 'tinymce/skins/ui/oxide/skin.min.css';
import 'tinymce/skins/ui/oxide/content.min.css';
import 'tinymce/skins/content/default/content.min.css';
import { DataFieldsEnum, InvoiceDataFieldsEnum } from 'src/shared/models/enums/dataFields.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { Error, Lock } from '@mui/icons-material';
import { createImage } from 'src/shared/services/imageService';
import { Colors } from '../styles/colors';

type CustomField = {
  label: string;
  value: string;
};

type ControlledTextEditorProps = {
  name: string;
  disabled?: boolean;
  smsMode?: boolean;
  eventMode?: boolean;
  adminInvoice?: boolean;
  adminInvoiceEmailSection?: boolean;
  noMargin?: boolean;
  rows?: number;
  resizeAllowed?: boolean;
  hideToolbarSettings?: boolean;
  hidedataPropFields?: boolean;
  allowedDataFields?: (DataFieldsEnum | InvoiceDataFieldsEnum)[];
  customFields?: CustomField[];
  // Rubrik på menyn med customFields. Utan den heter menyn Datafält, som i övriga editorer.
  customFieldsTitle?: string;
  hideMenuBar?: boolean;
  // Uttrycklig verktygsrad som ersätter presetsen (t.ex. 'bold' för en avskalad editor). Påverkar bara den instans
  // som skickar in propen – övriga editorer i systemet behåller sina presets.
  toolbarOverride?: string;
  // Egen uppladdning av bilder som ersätter husets bildarkiv (createImage). Får en fil och returnerar bildens adress.
  // Ett fel med meddelande visas i editorn; bilden tas då bort ur texten.
  imageUploadHandler?: (file: File) => Promise<string>;
  // Filtyper som editorn behandlar som bilder när de klistras in eller dras in.
  imageFileTypes?: string;
  // Adressen som relativa bildadresser i texten utgår från i editorn, t.ex. API:ts adress när bilderna sparas relativt den.
  documentBaseUrl?: string;
  // Knappen för video (mp4) i verktygsraden: laddar upp filen, visar förloppet och lägger in videon när den finns.
  // Får filen och en funktion för förloppet i procent och returnerar videons adress; ett fel med meddelande visas i
  // editorn. Utan den finns ingen videoknapp.
  videoUploadHandler?: (file: File, onProgress: (percent: number) => void) => Promise<string>;
  videoButtonText?: string;
  videoUploadingText?: string;
  // Element som tas bort ur texten, t.ex. 'iframe,object,embed' för att stoppa inbäddade spelare från andra sajter.
  invalidElements?: string;
  // Extra CSS för innehållet i editorn, t.ex. hur citatrutor ser ut. Läggs efter husets regler och påverkar bara den
  // instans som skickar in propen.
  contentStyle?: string;
  initCallback?: () => void;
  onChangeCallback?: (message: string) => void;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getMenuConfig = (
  customFields: CustomField[] | undefined,
  hidedataPropFields: boolean,
  allowedDataFields: (DataFieldsEnum | InvoiceDataFieldsEnum)[] | undefined,
  t: any,
  customFieldsTitle?: string,
) => {
  if (customFields) {
    return {
      custom: {
        title: customFieldsTitle ?? t('common:form.dataField'),
        items: customFields.map((_, i) => `customfield-${i}`).join(' '),
      },
    };
  }
  if (!hidedataPropFields) {
    return {
      custom: {
        title: t('common:form.dataField'),
        items: `file edit ${(allowedDataFields || Object.values(DataFieldsEnum)).map((item) => `datafield-${item}`).join(' ')}`,
      },
    };
  }
  return undefined;
};

const ControlledTextEditor = (props: ControlledTextEditorProps) => {
  const {
    name,
    disabled = false,
    smsMode = false,
    eventMode = false,
    adminInvoice = false,
    adminInvoiceEmailSection = false,
    noMargin = false,
    hideToolbarSettings = false,
    hidedataPropFields = false,
    allowedDataFields,
    customFields,
    customFieldsTitle = undefined,
    hideMenuBar = false,
    toolbarOverride = undefined,
    imageUploadHandler = undefined,
    imageFileTypes = 'jpg,jpeg,png,gif',
    documentBaseUrl = undefined,
    videoUploadHandler = undefined,
    videoButtonText = '',
    videoUploadingText = '',
    invalidElements = undefined,
    contentStyle = '',
    rows = 20,
    resizeAllowed = false,
    initCallback = undefined,
    onChangeCallback = undefined,
  } = props;
  const editorRef = useRef<TinyMCE | null>(null);
  const { t } = useTranslation(['common']);

  const { currentLanguage } = useGlobals();

  const [loading, setLoading] = useState<boolean>(true);
  const [language, setLanguage] = useState<string>('sv_SE');

  useEffect(() => {
    switch (currentLanguage) {
      case 'sv':
        setLanguage('sv_SE');
        break;
      case 'en':
        setLanguage('en_EN');
        break;
      default:
        setLanguage('en_EN');
    }
  }, [currentLanguage]);

  const { control } = useFormContext();
  const { errors } = useFormState();

  let isError = false;
  let errorMessage = '';
  if (errors && Object.prototype.hasOwnProperty.call(errors, name)) {
    isError = true;
    errorMessage = errors[name]?.message?.toString() || '';
  }

  const uploadImageToApi = async (file: File) => {
    const response = await createImage(file);
    return response.data.location;
  };

  const uploadImage = imageUploadHandler ?? uploadImageToApi;

  // Videon läggs in med kontroller och utan automatisk uppspelning; preload="metadata" hämtar bara längd och första
  // bildrutan tills någon trycker på play.
  const videoHtml = (source: string) => `<video controls="controls" preload="metadata"><source src="${source}" type="video/mp4" /></video>`;

  // Videon läggs in först när den finns, så att en halvfärdig uppladdning aldrig hamnar i texten.
  const pickAndUploadVideo = (editor: TinyMCEEditor) => {
    if (!videoUploadHandler) return;

    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'video/mp4,.mp4');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;

      const notification = editor.notificationManager.open({ text: videoUploadingText, type: 'info', progressBar: true });
      try {
        const url = await videoUploadHandler(file, (percent) => notification.progressBar.value(percent));
        notification.close();
        editor.insertContent(videoHtml(url));
      } catch (error) {
        notification.close();
        const message = (error as { message?: unknown })?.message;
        editor.notificationManager.open({ text: typeof message === 'string' ? message : '', type: 'error' });
      }
    };
    input.click();
  };

  const getMenuBar = (): string => {
    if (smsMode) return 'edit custom';
    if (eventMode) return '';
    // Fakturaeditorerna har ingen datafältsmeny som standard. Har de fått en lista med fält som
    // fungerar där ska menyn visas, annars går fälten inte att nå.
    if (adminInvoice || adminInvoiceEmailSection)
      return allowedDataFields ? 'file edit insert custom view format table tools help' : 'file edit insert view format table tools help';
    return 'file edit insert custom view format table tools help';
  };

  const getToolbarSettings = (): string => {
    if (smsMode) return 'undo redo | custom';
    if (eventMode) return 'bold italic underline | link | styleselect | typopgraphy | code |';
    if (adminInvoice)
      return 'insertfile undo redo | custom | styleselect | bold italic | forecolor backcolor| alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | emoticons';
    return `insertfile undo redo | custom | styleselect | bold italic | forecolor backcolor| alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image${
      videoUploadHandler ? ' uploadvideo' : ''
    } | emoticons`;
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => {
        return (
          <Box
            sx={{
              position: 'relative',
              '& .tox-tinymce': {
                ...(isError ? { borderColor: (theme) => theme.palette.error.dark } : null),
              },
            }}
          >
            {isError && (
              <Error
                sx={{
                  color: 'error.main',
                  position: 'absolute',
                  zIndex: 1000,
                  top: (theme) => theme.spacing(1),
                  right: (theme) => theme.spacing(1.5),
                }}
              />
            )}
            {disabled && !isError && (
              <Lock
                sx={{
                  color: 'text.disabled',
                  position: 'absolute',
                  zIndex: 1000,
                  top: (theme) => theme.spacing(1),
                  right: (theme) => theme.spacing(1.5),
                }}
              />
            )}
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: `10rem`,
                '& .tox-tinymce': {
                  borderRadius: 1,
                  borderColor: Colors.Form.OutlinedInput,
                },
                '& .tox-statusbar__branding': {
                  display: 'none',
                },
                '& .tox-tinymce--disabled': {
                  '& .tox-menubar, & .tox-toolbar__primary, & .tox-toolbar-overlord': {
                    backgroundColor: `${Colors.Form.DisabledInput} !important`,
                    opacity: 0.7,
                  },
                  '&.tox:not(.tox-tinymce-inline) .tox-editor-header': {
                    backgroundColor: `${Colors.Form.DisabledInput} !important`,
                  },
                },
                '& textarea': {
                  display: 'none',
                },
              }}
            >
              {loading && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: (thm) => thm.zIndex.modal,
                    backgroundColor: 'rgba(255,255,255,0.7)',
                  }}
                >
                  <CircularProgress
                    size='5rem'
                    sx={{
                      position: 'absolute',
                      left: 'calc(50% - 5rem)',
                      top: 'calc(50% - 5rem)',
                      zIndex: (thm) => thm.zIndex.modal + 1,
                    }}
                  />
                </Box>
              )}
              {disabled && !isError && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    pointerEvents: 'none',
                    zIndex: (thm) => thm.zIndex.mobileStepper - 10,
                  }}
                />
              )}
              <TinyMCE
                value={value}
                ref={editorRef}
                disabled={disabled}
                init={{
                  readonly: disabled,
                  height: `${rows}rem`,
                  resize: resizeAllowed,
                  convert_urls: false,
                  document_base_url: documentBaseUrl,
                  invalid_elements: invalidElements,
                  // Med videoknappen läggs video bara in via den; mediadialogen (Infoga → Media) skulle ta vilken adress
                  // som helst.
                  removed_menuitems: videoUploadHandler ? 'media' : undefined,
                  borderRadius: '4px',
                  statusbar: resizeAllowed,
                  elementpath: false,
                  language: `${language}`,
                  language_url: `/langs/${language}.js`,
                  noneditable_noneditable_class: 'is-locked',
                  paste_data_images: true,
                  automatic_uploads: true,
                  file_picker_types: 'image',
                  images_file_types: imageFileTypes,
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  file_picker_callback: (cb, imgValue, meta) => {
                    const input = document.createElement('input') as HTMLInputElement;
                    input.setAttribute('type', 'file');
                    input.setAttribute('accept', 'image/*');
                    input.onchange = () => {
                      const file = input?.files?.[0];
                      if (!file) return;
                      const reader = new FileReader();
                      reader.onload = (e: ProgressEvent<FileReader>) => {
                        if (e.target) {
                          // Skicka data-URLen till TinyMCE
                          cb(e.target.result, {
                            alt: file.name,
                          });
                        }
                      };
                      reader.readAsDataURL(file as Blob);
                    };
                    input.click();
                  },
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  images_upload_handler: async (blobInfo, success, failure, progress) => {
                    const blob = blobInfo.blob();
                    const file = new File([blob], 'image.jpg', { type: blob.type });
                    // Ladda upp filen till API och få tillbaka en URL
                    try {
                      const uploadedUrl = await uploadImage(file);
                      // Använd den uppladdade bildens URL som källa i editorn
                      success(uploadedUrl);
                    } catch (error) {
                      // En egen uppladdning ger ett meddelande som går att visa; husets bildarkiv ger det inte. (Error är
                      // MUI-ikonen i den här filen, så instanceof Error går inte att använda.)
                      const message = (error as { message?: unknown })?.message;
                      failure(imageUploadHandler && typeof message === 'string' && message ? message : 'Image upload failed', { remove: true });
                    }
                  },
                  paste_as_text: smsMode,
                  // Tvingar webbläsarens native contextmenu vid högerklick så att
                  // Klistra in/Paste finns kvar. Link-pluginen auto-registrerar
                  // annars en egen "Länk"-post som kapar högerklick-menyn.
                  contextmenu: '',
                  contextmenu_never_use_native: false,
                  browser_spellcheck: true,
                  // eslint-disable-next-line no-nested-ternary
                  menubar: hideMenuBar ? false : customFields ? 'file edit insert custom view format table tools help' : getMenuBar(),
                  menu: getMenuConfig(customFields, hidedataPropFields, allowedDataFields, t, customFieldsTitle),
                  noneditable_regexp: /\[[^\]]+\]/g,
                  // noneditable_regexp: /\[[^}]+\]/g,
                  setup: (editor) => {
                    editor.on('init', () => {
                      setLoading(false);
                      if (initCallback !== undefined) initCallback();
                    });
                    if (videoUploadHandler) {
                      editor.ui.registry.addButton('uploadvideo', {
                        icon: 'embed',
                        tooltip: videoButtonText,
                        onAction: () => pickAndUploadVideo(editor),
                      });
                      // Mediapluginen ger varje video måtten 300×150 och en inklistrad video kan ha autoplay. Videon ska
                      // fylla artikelns bredd och aldrig starta av sig själv, så de tas bort när texten sparas.
                      editor.on('PostProcess', (e: { content: string }) => {
                        e.content = e.content.replace(/<video\b[^>]*>/g, (tag) => tag.replace(/\s(width|height|autoplay)(="[^"]*")?/g, ''));
                      });
                    }
                    if (customFields) {
                      customFields.forEach((field, i) => {
                        editor.ui.registry.addMenuItem(`customfield-${i}`, {
                          type: 'menuitem',
                          text: field.label,
                          onAction() {
                            editor.insertContent(field.value);
                          },
                        });
                      });
                    } else if (!eventMode) {
                      (allowedDataFields || Object.values(DataFieldsEnum)).map((item) => {
                        editor.ui.registry.addMenuItem(`datafield-${item}`, {
                          type: 'menuitem',
                          text: t(`dataFields.${item}`),
                          onAction() {
                            editor.insertContent(`[prop.${item}]`);
                          },
                        });
                        return '';
                      });
                    }
                  },
                  width: '100%',
                  // media visar videon i editorn och gör den möjlig att markera och ta bort; bara där video kan läggas in.
                  plugins: [`emoticons noneditable code lists link image paste${videoUploadHandler ? ' media' : ''}`],
                  // eslint-disable-next-line no-nested-ternary
                  toolbar: hideToolbarSettings === true ? false : toolbarOverride !== undefined ? toolbarOverride : getToolbarSettings(),
                  content_style: `
                  .is-locked,
                  .mceNonEditable {
                      background-color: #D6F0FF;
                      padding: 1px 0;
                      color: #44719B;
                      font-family: SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
                      font-size: 0.9375em;
                  }
                  video { max-width: 100%; height: auto; }
                  body { padding: 0; ${noMargin && 'margin: 0'} },
                  body.mce-content-readonly {
                    color: rgba(0, 0, 0, 0.38);
                    background-color: ${Colors.Form.DisabledInput};
                  }
                  ${contentStyle}
                `,
                }}
                onEditorChange={(content, editor) => {
                  onChange(content);
                  if (onChangeCallback) onChangeCallback(editor.getContent({ format: 'text' }));
                }}
              />
            </Box>
            {isError && (
              <FormHelperText error={isError} sx={{ pl: 2, pt: 0.5 }}>
                <em>{errorMessage}</em>
              </FormHelperText>
            )}
          </Box>
        );
      }}
    />
  );
};

export default ControlledTextEditor;
